import { CheckCircle, ExternalLink, Share2, Download, Star, TrendingUp } from 'lucide-react';
import { ProjectData } from '../types';
import { resolveSupportContact, supportPhoneTelHref } from '../utils/supportContact';

interface DeploymentFinishedProps {
  project: ProjectData;
  appStoreLinks?: {
    apple?: string;
    google?: string;
  };
}

export const DeploymentFinished = ({ project, appStoreLinks }: DeploymentFinishedProps) => {
  const { appType, jiraKey } = project;
  const appName = appType === 'versirent' ? 'VersiRent' : 'IdealOne';
  const support = resolveSupportContact(appType, project.supportContact);

  const handleShare = async () => {
    const shareData = {
      title: `${appName} App is Live!`,
      text: `Check out my new ${appName} app! It's now available for download on the App Stores.`,
      url: window.location.href
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.log('Error sharing:', err);
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(`${shareData.text} ${shareData.url}`);
      alert('Link copied to clipboard!');
    }
  };

  return (
    <div className="space-y-6">
      {/* Celebration Banner */}
      <div className="bg-gradient-to-r from-emerald-500 to-green-600 rounded-lg p-8 text-white text-center">
        <div className="text-6xl mb-4">🎉</div>
        <h2 className="text-3xl font-bold mb-2">Congratulations!</h2>
        <p className="text-xl opacity-90">
          Your {appName} app is now live and available for download!
        </p>
      </div>

      {/* App Store Links */}
      {appStoreLinks && (appStoreLinks.apple || appStoreLinks.google) && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">
            📱 Download Your App
          </h3>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            {appStoreLinks.apple && (
              <a
                href={appStoreLinks.apple}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-3 bg-black hover:bg-gray-800 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200"
              >
                <span className="text-2xl">🍎</span>
                <div className="text-left">
                  <div className="text-sm opacity-75">Download on the</div>
                  <div className="font-semibold">App Store</div>
                </div>
              </a>
            )}
            {appStoreLinks.google && (
              <a
                href={appStoreLinks.google}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-3 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200"
              >
                <span className="text-2xl">🤖</span>
                <div className="text-left">
                  <div className="text-sm opacity-75">GET IT ON</div>
                  <div className="font-semibold">Google Play</div>
                </div>
              </a>
            )}
          </div>
        </div>
      )}

      {/* Project Completion Summary */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">
          ✅ Project Completion Summary
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span className="text-gray-900">App Development Complete</span>
            </div>
            <div className="flex items-center space-x-3">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span className="text-gray-900">App Store Submission Complete</span>
            </div>
            <div className="flex items-center space-x-3">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span className="text-gray-900">Apple/Google Review Passed</span>
            </div>
          </div>
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span className="text-gray-900">App Successfully Deployed</span>
            </div>
            <div className="flex items-center space-x-3">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span className="text-gray-900">Available for Download</span>
            </div>
            <div className="flex items-center space-x-3">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span className="text-gray-900">Project Status: Complete</span>
            </div>
          </div>
        </div>
      </div>

      {/* Share Success */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">
          🎊 Share Your Success!
        </h3>
        <p className="text-blue-800 mb-4">
          Your app is now live! Share the news with your users, customers, and network.
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={handleShare}
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
          >
            <Share2 className="h-4 w-4" />
            <span>Share News</span>
          </button>
          <a
            href={`/project/${jiraKey}/help#marketing-tips`}
            className="flex items-center space-x-2 bg-blue-100 hover:bg-blue-200 text-blue-800 px-4 py-2 rounded-lg transition-colors duration-200"
          >
            <TrendingUp className="h-4 w-4" />
            <span>Marketing Tips</span>
          </a>
        </div>
      </div>

      {/* Next Steps */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">
          🎯 What's Next?
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="bg-green-100 text-green-800 rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold mt-0.5">
                1
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Monitor Performance</h4>
                <p className="text-gray-600 text-sm">Keep an eye on downloads, user reviews, and app performance metrics</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-green-100 text-green-800 rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold mt-0.5">
                2
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Gather User Feedback</h4>
                <p className="text-gray-600 text-sm">Listen to user reviews and feedback to identify areas for improvement</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-green-100 text-green-800 rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold mt-0.5">
                3
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Plan Future Updates</h4>
                <p className="text-gray-600 text-sm">Consider new features and improvements based on user needs</p>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="bg-green-100 text-green-800 rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold mt-0.5">
                4
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Marketing & Promotion</h4>
                <p className="text-gray-600 text-sm">Promote your app through your channels and marketing efforts</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-green-100 text-green-800 rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold mt-0.5">
                5
              </div>
              <div>
                <h4 className="font-medium text-gray-900">User Support</h4>
                <p className="text-gray-600 text-sm">Be ready to help users with questions and technical support</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="bg-green-100 text-green-800 rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold mt-0.5">
                6
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Maintenance</h4>
                <p className="text-gray-600 text-sm">Keep your app updated and maintain compatibility with OS updates</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* App Store Optimization Tips */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-amber-900 mb-3">
          📈 App Store Optimization Tips
        </h3>
        <div className="space-y-3">
          <div className="flex items-start space-x-3">
            <Star className="h-5 w-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-medium text-amber-900">Encourage Reviews</p>
              <p className="text-amber-800 text-sm">Ask satisfied users to leave positive reviews on the App Store</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <TrendingUp className="h-5 w-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-medium text-amber-900">Monitor Analytics</p>
              <p className="text-amber-800 text-sm">Use App Store analytics to track downloads, user engagement, and retention</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <Download className="h-5 w-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-medium text-amber-900">Update Regularly</p>
              <p className="text-amber-800 text-sm">Keep your app updated with bug fixes and new features</p>
            </div>
          </div>
        </div>
      </div>

      {/* Support & Resources */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          📚 Support & Resources
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <a
              href={`/project/${jiraKey}/help#post-launch-support`}
              className="flex items-center space-x-3 p-3 bg-white rounded-lg border hover:border-blue-300 transition-colors duration-200"
            >
              <div className="bg-blue-100 text-blue-800 rounded-full w-8 h-8 flex items-center justify-center">
                🆘
              </div>
              <div>
                <p className="font-medium text-gray-900">Post-Launch Support</p>
                <p className="text-sm text-gray-600">Get help with ongoing app maintenance and updates</p>
              </div>
              <ExternalLink className="h-4 w-4 text-gray-400 ml-auto" />
            </a>
            <a
              href="https://developer.apple.com/app-store-optimization/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-3 p-3 bg-white rounded-lg border hover:border-blue-300 transition-colors duration-200"
            >
              <div className="bg-blue-100 text-blue-800 rounded-full w-8 h-8 flex items-center justify-center">
                📊
              </div>
              <div>
                <p className="font-medium text-gray-900">App Store Analytics</p>
                <p className="text-sm text-gray-600">Learn about Apple's App Store analytics tools</p>
              </div>
              <ExternalLink className="h-4 w-4 text-gray-400 ml-auto" />
            </a>
          </div>
          <div className="space-y-3">
            <a
              href="https://support.google.com/googleplay/android-developer/answer/4448378"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-3 p-3 bg-white rounded-lg border hover:border-blue-300 transition-colors duration-200"
            >
              <div className="bg-blue-100 text-blue-800 rounded-full w-8 h-8 flex items-center justify-center">
                📈
              </div>
              <div>
                <p className="font-medium text-gray-900">Google Play Console</p>
                <p className="text-sm text-gray-600">Access Google Play Store analytics and tools</p>
              </div>
              <ExternalLink className="h-4 w-4 text-gray-400 ml-auto" />
            </a>
            <a
              href={`/project/${jiraKey}/help#future-updates`}
              className="flex items-center space-x-3 p-3 bg-white rounded-lg border hover:border-blue-300 transition-colors duration-200"
            >
              <div className="bg-blue-100 text-blue-800 rounded-full w-8 h-8 flex items-center justify-center">
                🔄
              </div>
              <div>
                <p className="font-medium text-gray-900">Future Updates</p>
                <p className="text-sm text-gray-600">Learn about planning and deploying app updates</p>
              </div>
              <ExternalLink className="h-4 w-4 text-gray-400 ml-auto" />
            </a>
          </div>
        </div>
      </div>

      {/* Ongoing Support */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-emerald-900 mb-3">
          🤝 Ongoing Support
        </h3>
        <p className="text-emerald-800 mb-4">
          Even though your project is complete, we're still here to help with future updates, technical support, or any questions about your live app.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center space-x-3">
            <div className="bg-emerald-100 text-emerald-800 rounded-full w-8 h-8 flex items-center justify-center">
              ✉️
            </div>
            <div>
              <p className="font-medium text-emerald-900">Email Support</p>
              <p className="text-sm text-emerald-700">
                <a href={`mailto:${support.email}`} className="hover:text-emerald-900">
                  {support.email}
                </a>
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="bg-emerald-100 text-emerald-800 rounded-full w-8 h-8 flex items-center justify-center">
              📞
            </div>
            <div>
              <p className="font-medium text-emerald-900">Phone Support</p>
              <p className="text-sm text-emerald-700">
                <a href={supportPhoneTelHref(support.phone)} className="hover:text-emerald-900">
                  {support.phone}
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
